import { MenuItem } from "@/redux/services/menu/menu.type";
import { cn, displayPrice, getImageUrl, getMenuItemDisplayPrice } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";

interface PosItemCardProps {
  item: MenuItem;
  sectionTitle?: string;
  currency: string;
  quantity: number;
  hasActiveOrder: boolean;
  onCardClick: () => void;
  onUpdateQuantity: (delta: number) => void;
}

const PosItemCard: React.FC<PosItemCardProps> = ({
  item,
  sectionTitle,
  currency,
  quantity,
  hasActiveOrder,
  onCardClick,
  onUpdateQuantity,
}) => {
  const isUnavailable = !item.isAvailable;

  const displayedPrice = getMenuItemDisplayPrice(item);

  return (
    <div
      onClick={onCardClick}
      className={cn(
        "rounded-xl border bg-card p-3 flex flex-col items-center gap-3 transition-all cursor-pointer group relative",
        isUnavailable && "pointer-events-none",
        quantity > 0
          ? "border-primary shadow-sm"
          : "border-border hover:border-primary/30 hover:shadow-sm"
      )}
    >
      <div className="w-full bg-gray-50 rounded-lg flex items-center justify-center py-2 lg:py-4">
        <div className="w-24 h-24 rounded-full overflow-hidden relative">
          <img
            src={getImageUrl(item.image?.url)}
            alt={item.title}
            className={cn(
              "w-full h-full object-cover",
              isUnavailable && "grayscale"
            )}
          />
          {isUnavailable && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
              <span className="text-white text-xs font-medium px-2 py-0.5 bg-destructive rounded">
                Indisponible
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="w-full text-left space-y-0.5">
        {sectionTitle && (
          <p className="text-xs text-muted-foreground font-medium">
            {sectionTitle}
          </p>
        )}
        <p className="text-sm font-semibold truncate">
          {item.title}
        </p>
      </div>

      <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mt-auto">
        <span className="text-sm font-semibold">
          {displayPrice(displayedPrice, currency)}
        </span>

        {!isUnavailable && hasActiveOrder && (
          <div
            className={cn(
              "flex items-center gap-2 transition-opacity",
              quantity === 0 && "lg:opacity-0 lg:group-hover:opacity-100"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => onUpdateQuantity(-1)}
              className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            >
              <Minus size={14} />
            </button>
            <span className="text-sm font-medium w-4 text-center">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(1)}
              className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
            >
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PosItemCard;
